import React, { createContext, useContext } from 'react';
import type { Message, ToolCall, Attachment, Theme } from '../types';

// ---- Config context (rarely changes) ----
export interface ChatConfigValue {
  enableToolVisualization: boolean;
  enableSourceCitations: boolean;
  enableThinking: boolean;
  showTimestamps: boolean;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
}

const ChatConfigContext = createContext<ChatConfigValue | null>(null);

export function ChatConfigProvider({
  value,
  children,
}: {
  value: ChatConfigValue;
  children: React.ReactNode;
}) {
  return <ChatConfigContext.Provider value={value}>{children}</ChatConfigContext.Provider>;
}

export function useChatConfig(): ChatConfigValue {
  const ctx = useContext(ChatConfigContext);
  if (!ctx) throw new Error('useChatConfig must be used within a ChatConfigProvider');
  return ctx;
}

// ---- State context (changes frequently) ----
export interface ChatStateValue {
  messages: Message[];
  input: string;
  loading: boolean;
  error: string;
  isTyping: boolean;
  streamingMessage: Message | null;
  activeToolCalls: ToolCall[];
  attachments: Attachment[];
  view: string;
  theme: Theme;
}

const ChatStateContext = createContext<ChatStateValue | null>(null);

export function ChatStateProvider({
  value,
  children,
}: {
  value: ChatStateValue;
  children: React.ReactNode;
}) {
  return <ChatStateContext.Provider value={value}>{children}</ChatStateContext.Provider>;
}

export function useChatState(): ChatStateValue {
  const ctx = useContext(ChatStateContext);
  if (!ctx) throw new Error('useChatState must be used within a ChatStateProvider');
  return ctx;
}

// ---- Combined provider ----
export interface ChatContextValue extends ChatConfigValue, ChatStateValue {}

export function ChatProvider({
  value,
  children,
}: {
  value: ChatContextValue;
  children: React.ReactNode;
}) {
  const configValue: ChatConfigValue = {
    enableToolVisualization: value.enableToolVisualization,
    enableSourceCitations: value.enableSourceCitations,
    enableThinking: value.enableThinking,
    showTimestamps: value.showTimestamps,
    trackAnalytics: value.trackAnalytics,
  };

  const stateValue: ChatStateValue = {
    messages: value.messages,
    input: value.input,
    loading: value.loading,
    error: value.error,
    isTyping: value.isTyping,
    streamingMessage: value.streamingMessage,
    activeToolCalls: value.activeToolCalls,
    attachments: value.attachments,
    view: value.view,
    theme: value.theme,
  };

  return (
    <ChatConfigProvider value={configValue}>
      <ChatStateProvider value={stateValue}>{children}</ChatStateProvider>
    </ChatConfigProvider>
  );
}

// Backwards-compatible combined hook
export function useChatContext(): ChatContextValue {
  const config = useChatConfig();
  const state = useChatState();
  return { ...config, ...state };
}
